import React, { useState, useEffect } from 'react';
import InputField from './ui/InputField';
import EmailInput from './ui/EmailInput';
import SelectField from './ui/SelectField';
import Slider from './ui/Slider';
import Checkbox from './ui/Checkbox';
import FileUpload from './ui/FileUpload';
import Button from './ui/Button';
import BankSelection from './ui/BankSelection';
import PhoneInput from './ui/PhoneInput';
import Alert from './ui/Alert';
import { LoanFormData, FormErrors, BankInstitution, LoanType } from '../types';
import { validateForm, formatPersonalCode } from '../utils/formValidation';
import { submitLoanApplication, fetchAvailableInstitutions } from '../utils/api';
import { checkFormSubmissionLimit, recordFormSubmission, formatTimeUntilReset } from '../utils/formSubmissionLimits';
import { t, getCurrentLanguage } from '../utils/i18n';
import {
  trackFormStart,
  trackFormStepCompleted,
  trackFormStepBack,
  trackFormSubmittedUnverified,
  trackFormSubmitError,
} from '../utils/gtm';


// Function to detect if the device is mobile
const isMobileDevice = (): boolean => {
  return window.innerWidth <= 768;
};

interface LoanFormProps {
  onSubmit: (formData: LoanFormData) => void;
  onLoading: (isLoading: boolean, message?: string) => void;
  isInPopup?: boolean;
  defaultLoanType?: LoanType;
  onClose?: () => void;
}

const LoanForm: React.FC<LoanFormProps> = ({ onSubmit, onLoading, isInPopup = false, defaultLoanType, onClose }) => {
  // Adaptable affiliate detection: returns source name and optional id.
  // Add new sources by checking query params and returning the same shape.
  const AFFILIATE_SOURCES = {
    doaffiliate: 'doaffiliate',  // ?v=... or utm_source=doaff
    googleads: 'googleads',      // utm_source=adwords&utm_medium=ppc (also google/cpc)
    banner: 'Banner',
    inbox: 'inbox',
    facebookads: 'Facebook Ads'
  } as const;

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    const hashQueryIndex = hash.indexOf('?');
    if (hashQueryIndex !== -1) {
      const hashParams = new URLSearchParams(hash.slice(hashQueryIndex + 1));
      hashParams.forEach((value, key) => {
        if (!params.has(key)) {
          params.set(key, value);
        }
      });
    }
    return params;
  };

  const getAffiliateFromUrl = (): { source: string; id: string } => {
    const urlParams = getUrlParams();
    const vParam = urlParams.get('v') ?? '';
    const utmSource = (urlParams.get('utm_source') ?? '').trim().toLowerCase();
    const utmMedium = (urlParams.get('utm_medium') ?? '').trim().toLowerCase();

    const isDoaff =
      Boolean(vParam) ||
      utmSource === 'doaff' ||
      (utmSource === 'doaffiliate' && utmMedium === 'affiliate');
    if (isDoaff) {
      return { source: AFFILIATE_SOURCES.doaffiliate, id: vParam };
    }

    const affiliateSource = urlParams.get('affiliate_source');
    if (affiliateSource) {
      const normalized = affiliateSource.trim().toLowerCase();
      const matchedSource = Object.entries(AFFILIATE_SOURCES).find(
        ([key, value]) => normalized === key || normalized === value.toLowerCase()
      );
      if (matchedSource) {
        return { source: matchedSource[1], id: '' };
      }
    }

    const hsaNet = (urlParams.get('hsa_net') ?? '').trim().toLowerCase();
    const isGoogleAds =
      hsaNet === 'adwords' ||
      (utmSource === 'adwords' && utmMedium === 'ppc') ||
      (utmSource === 'google' && utmMedium === 'cpc');
    if (isGoogleAds) {
      return { source: AFFILIATE_SOURCES.googleads, id: '' };
    }
    return { source: '', id: '' };
  };

  const initialAffiliate = getAffiliateFromUrl();

  const initialFormState: LoanFormData = {
    firstName: '',
    lastName: '',
    personalCode: '',
    email: '',
    phone: '+371',
    city: '',
    loanAmount: 8000,
    loanPeriod: 24,
    loanType: defaultLoanType || 'credit_consolidation',
    declaredAddress: '',
    facticalAddress: '',
    addressesMatch: true,
    dependentPersons: 0,
    monthlyIncome: '',
    monthlyPayments: '',
    bankAccount: '',
    documents: [],
    agreeToTerms: false,
    familyStatus: '',
    jobType: '',
    hasRealEstate: false,
    isPoliticallyImportant: false,
    authorizeBankAccess: true,
    selectedBank: '',
    affiliateSource: initialAffiliate.source,
    affiliateId: initialAffiliate.id,
  };
  
  const [formData, setFormData] = useState<LoanFormData>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [maxLoanAmount, setMaxLoanAmount] = useState(20000);
  const [minLoanAmount, setMinLoanAmount] = useState(100);
  const [stepSize, setStepSize] = useState(100);
  const [maxLoanPeriod, setMaxLoanPeriod] = useState(120);
  const [currentStep, setCurrentStep] = useState(1);
  const [formApplicantId, setformApplicantId] = useState<string | null>(() => {
    return localStorage.getItem('formApplicantId');
  });
  const [bankInstitutions, setBankInstitutions] = useState<BankInstitution[]>([]);
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [submissionLimit, setSubmissionLimit] = useState(() => checkFormSubmissionLimit());
  const [flashAlert, setFlashAlert] = useState<{
    visible: boolean;
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
  }>({
    visible: false,
    type: 'info',
    message: '',
  });
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);

  useEffect(() => {
    trackFormStart({
      loanType: defaultLoanType || 'credit_consolidation',
      loanAmount: 8000,
    });
  }, [defaultLoanType]);

  const stepFields = {
    1: ['firstName', 'lastName', 'personalCode', 'email', 'phone', 'agreeToTerms'],
    2: ['loanType', 'loanAmount', 'loanPeriod'],
    3: ['city', 'declaredAddress'],
    4: ['dependentPersons', 'familyStatus', 'jobType', 'monthlyIncome', 'monthlyPayments', 'hasRealEstate', 'isPoliticallyImportant'],
    5: ['bankAccount', 'documents', 'agreeToTerms', 'authorizeBankAccess'],
  };

  const loanTypeOptions = [
    { value: 'credit_consolidation', label: t('loan_form.loan_types.credit_consolidation') },
    { value: 'consumer', label: t('loan_form.loan_types.consumer') },
    { value: 'mortgage', label: t('loan_form.loan_types.mortgage') },
    { value: 'carLeasing', label: t('loan_form.loan_types.carLeasing') },
    { value: 'business', label: t('loan_form.loan_types.business') },
  ];
  
  const dependentPersonsOptions = [
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5+' },
  ];

  const familyStatusOptions = [
    { value: 'single', label: t('loan_form.family_status.single') },
    { value: 'married', label: t('loan_form.family_status.married') },
  ];

  const jobTypeOptions = [
    { value: 'permanent', label: t('loan_form.job_types.permanent') },
    { value: 'temporary', label: t('loan_form.job_types.temporary') },
    { value: 'self_employed', label: t('loan_form.job_types.self_employed') },
    { value: 'retired', label: t('loan_form.job_types.retired') },
    { value: 'student', label: t('loan_form.job_types.student') },
    { value: 'unemployed', label: t('loan_form.job_types.unemployed') },
    { value: 'other', label: t('loan_form.job_types.other') },
  ];
  
  useEffect(() => {
    const loanTypeConfig = {
      credit_consolidation: { max: 10000, step: 500, min: 1000, maxPeriod: 84 },
      consumer: { max: 20000, step: 100, min: 100, maxPeriod: 84 },
      mortgage: { max: 500000, step: 5000, min: 1000, maxPeriod: 120 },
      carLeasing: { max: 100000, step: 500, min: 500, maxPeriod: 120 },
      business: { max: 400000, step: 5000, min: 1000, maxPeriod: 120 },
    };
    
    const config = loanTypeConfig[formData.loanType];
    setMaxLoanAmount(config.max);
    setStepSize(config.step);
    setMinLoanAmount(config.min);
    setMaxLoanPeriod(config.maxPeriod);
    
    if (formData.loanAmount > config.max) {
      setFormData(prev => ({
        ...prev,
        loanAmount: config.max,
      }));
    }
    
    if (formData.loanPeriod > config.maxPeriod) {
      setFormData(prev => ({
        ...prev,
        loanPeriod: config.maxPeriod,
      }));
    }
  }, [formData.loanType]);
  
  useEffect(() => {
    if (formApplicantId) {
      localStorage.setItem('formApplicantId', formApplicantId);
    } else {
      localStorage.removeItem('formApplicantId');
    }
  }, [formApplicantId]);
  
  useEffect(() => {
    const loadInstitutions = async () => {
      setLoadingInstitutions(true);
      try {
        const response = await fetchAvailableInstitutions();
        if (response.success && response.data) {
          setBankInstitutions(response.data.institutions);
        } else {
          console.error('Failed to load bank institutions:', response.error);
        }
      } catch (error) {
        console.error('Error loading bank institutions:', error);
      } finally {
        setLoadingInstitutions(false);
      }
    };

    // Load institutions when component mounts or when reaching step 5
    if (currentStep === 5 && bankInstitutions.length === 0) {
      loadInstitutions();
    }
  }, [currentStep, bankInstitutions.length]);

  // Check submission limits when component mounts
  useEffect(() => {
    const limit = checkFormSubmissionLimit();
    setSubmissionLimit(limit);
  }, []);

  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'bankAccount') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
    } else if (name === 'personalCode') {
      setFormData(prev => ({ ...prev, [name]: formatPersonalCode(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    setTouched(prev => ({ ...prev, [name]: true }));
  };
  
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const newErrors = validateForm(formData);
    setErrors(prev => ({
      ...prev,
      [name]: newErrors[name]
    }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };
  
  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData(prev => ({ ...prev, [name]: checked }));
    setTouched(prev => ({ ...prev, [name]: true }));
    
    if (name === 'addressesMatch' && checked) {
      setFormData(prev => ({ ...prev, facticalAddress: prev.declaredAddress }));
    }
    
    // Clear bank selection when bank authorization is unchecked
    if (name === 'authorizeBankAccess' && !checked) {
      setFormData(prev => ({ ...prev, selectedBank: '' }));
      setTouched(prev => ({ ...prev, selectedBank: false }));
    }
  };
  
  const handleFileChange = (files: File[]) => {
    setFormData(prev => {
      const existingFiles = prev.documents;
      const newFiles = files.filter(file => 
        !existingFiles.some(existing => 
          existing.name === file.name && existing.size === file.size
        )
      );
      
      return {
        ...prev, 
        documents: [...existingFiles, ...newFiles]
      };
    });
    setTouched(prev => ({ ...prev, documents: true }));
  };
  
  useEffect(() => {
    const touchedFields = Object.keys(touched).filter(key => touched[key]);
    if (touchedFields.length > 0) {
      const newErrors = validateForm(formData);
      const filteredErrors: FormErrors = {};
      
      touchedFields.forEach(field => {
        if (newErrors[field]) {
          filteredErrors[field] = newErrors[field];
        }
      });
      
      setErrors(filteredErrors);
    }
  }, [formData, touched]);
  
  const validateStep = (step: number): boolean => {
    let fieldsToValidate = [...stepFields[step as keyof typeof stepFields]];
    const newErrors = validateForm(formData);
    
    // For step 5, also validate selectedBank if authorizeBankAccess is true
    if (step === 5 && formData.authorizeBankAccess) {
      fieldsToValidate.push('selectedBank');
    }
    
    // For step 5, don't require documents if bank authorization is enabled
    if (step === 5 && formData.authorizeBankAccess) {
      fieldsToValidate = fieldsToValidate.filter(field => field !== 'documents');
    }
    
    const hasStepErrors = fieldsToValidate.some(field => {
      const hasError = newErrors[field];
      if (hasError) {
      }
      return hasError;
    });
    return !hasStepErrors;
  };
  
  const handleNextStep = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // Check submission limits before allowing progression from step 1
    if (currentStep === 1) {
      const currentLimit = checkFormSubmissionLimit();
      if (!currentLimit.canSubmit) {
        const timeRemaining = formatTimeUntilReset(currentLimit.timeUntilReset);
        setFlashAlert({
          visible: true,
          type: 'warning',
          message: `${t('loan_form.limit_exceeded')} ${t('loan_form.try_again_in')}: ${timeRemaining}`,
        });
        return;
      }
    }

    const currentStepFields = stepFields[currentStep as keyof typeof stepFields];
    let fieldsToTouch = [...currentStepFields];
    
    // For step 5, also mark selectedBank as touched if bank authorization is enabled
    if (currentStep === 5 && formData.authorizeBankAccess) {
      fieldsToTouch.push('selectedBank');
    }
    
    setTouched(prev => ({
      ...prev,
      ...Object.fromEntries(fieldsToTouch.map(field => [field, true]))
    }));

    const isStepValid = validateStep(currentStep);

    if (isStepValid) {
      trackFormStepCompleted(currentStep, formData);

      if (currentStep === 1) {
        const formDataToSubmit = new FormData();
        Object.entries(formData).forEach(([key, value]) => {
          if (['firstName', 'lastName', 'personalCode', 'email', 'phone', 'city', 'affiliateSource', 'affiliateId'].includes(key)) {
            formDataToSubmit.append(key, String(value));
          }
        });
        
        submitLoanApplication(formDataToSubmit)
          .then(response => {
            if (response.success && response.data?.contact_id) {
              setformApplicantId(response.data.contact_id);
            } else {
              console.error('API Error - Success:', response.success, 'Data:', response.data, 'Error:', response.error);
            }
          })
          .catch(error => {
            console.error('Error:', error);
          });
        
        setCurrentStep(2);
        // Scroll to top of form only on mobile
        if (isMobileDevice()) {
          const formElement = document.getElementById('loan-application-form');
          if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      } else if (currentStep < 5) {
        setCurrentStep(prev => prev + 1);
        // Scroll to top of form only on mobile
        if (isMobileDevice()) {
          const formElement = document.getElementById('loan-application-form');
          if (formElement) {
            formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }
    }
  };
  
  const handlePrevStep = () => {
    trackFormStepBack(currentStep, formData);
    setCurrentStep(prev => prev - 1);
    // Scroll to top of form only on mobile
    if (isMobileDevice()) {
      const formElement = document.getElementById('loan-application-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check submission limits first - critical safety check
    const currentLimit = checkFormSubmissionLimit();
    if (!currentLimit.canSubmit) {
      const timeRemaining = formatTimeUntilReset(currentLimit.timeUntilReset);
      setFlashAlert({
        visible: true,
        type: 'error',
        message: `${t('loan_form.limit_exceeded')} ${t('loan_form.try_again_in')}: ${timeRemaining}`,
      });
      // Explicitly stop any further processing
      return false;
    }
    
    // Mark all required fields as touched for final validation
    const currentStepFields = stepFields[currentStep as keyof typeof stepFields];
    let fieldsToTouch = [...currentStepFields];
    
    // For step 5, also mark selectedBank as touched if bank authorization is enabled
    if (currentStep === 5 && formData.authorizeBankAccess) {
      fieldsToTouch.push('selectedBank');
    }
    
    setTouched(prev => ({
      ...prev,
      ...Object.fromEntries(fieldsToTouch.map(field => [field, true]))
    }));
    
    if (!validateStep(currentStep)) {
      return;
    }
    
    try {
      onLoading(true, t('loan_form.processing'));
      
      const formDataToSubmit = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key !== 'documents') {
          formDataToSubmit.append(key, String(value));
        }
      });
      
      formData.documents.forEach(file => {
        formDataToSubmit.append('documents[]', file);
      });
      
      if (formApplicantId) {
        formDataToSubmit.append('formApplicationId', formApplicantId);
      }
      
      formDataToSubmit.append('authorizeBankAccess', String(formData.authorizeBankAccess));
      
      if (formData.authorizeBankAccess && formData.selectedBank) {
        const selectedBank = bankInstitutions.find(bank => bank.id === formData.selectedBank);
        if (selectedBank) {
          formDataToSubmit.append('bankId', formData.selectedBank);
        }
      }
      
      const response = await submitLoanApplication(formDataToSubmit);
      if (response.success) {
        // Record the successful submission
        recordFormSubmission();
        
        // Update submission limit state
        setSubmissionLimit(checkFormSubmissionLimit());
        
        // Mark form as successfully submitted to disable buttons
        setIsSuccessfullySubmitted(true);
        
        localStorage.removeItem('formApplicantId');
        setformApplicantId(null);

        trackFormStepCompleted(5, formData);
        trackFormSubmittedUnverified(formData, {
          goingToBankVerification: Boolean(response.data?.redirect_url),
        });
        
        // Show success message
        setFlashAlert({
          visible: true,
          type: 'success',
          message: t('loan_form.messages.success') || 'Application submitted successfully!',
        });
        
        // Wait a bit before redirecting to show the success message
        setTimeout(() => {
          if (response.data?.redirect_url) {
            // Use redirect URL from API response if provided
            window.location.href = response.data.redirect_url;
          } else {
            // Otherwise redirect to the submitted page
            const locale = getCurrentLanguage();
            window.location.href = `/${locale}/submitted`;
          }
        }, 100);
      } else {
        trackFormSubmitError(formData, response.error);
        // Show error message
        setFlashAlert({
          visible: true,
          type: 'error',
          message: response.error || t('loan_form.messages.error') || 'An error occurred. Please try again.',
        });
        console.error('Error:', response.error || 'Unknown error occurred');
      }
    } catch (error) {
      trackFormSubmitError(
        formData,
        error instanceof Error ? error.message : 'network_error'
      );
      // Show error message
      setFlashAlert({
        visible: true,
        type: 'error',
        message: t('loan_form.messages.error') || 'An error occurred. Please try again.',
      });
      console.error('Error:', error);
    } finally {
      onLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
  return (
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-4 sm:mb-6 text-center">{t('loan_form.step_titles.personal_info')}</h3>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-6 sm:mb-8">{t('loan_form.step_descriptions.personal_info')}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <InputField
            label={t('loan_form.fields.first_name')}
                name="firstName"
            value={formData.firstName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.firstName ? errors.firstName : undefined}
            required
          />
              
          <InputField
            label={t('loan_form.fields.last_name')}
                name="lastName"
            value={formData.lastName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.lastName ? errors.lastName : undefined}
            required
          />
        </div>

            <InputField
              label={t('loan_form.fields.personal_code')}
              name="personalCode"
              value={formData.personalCode}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={touched.personalCode ? errors.personalCode : undefined}
              placeholder={t('loan_form.placeholders.personal_code')}
              required
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <EmailInput
                label={t('loan_form.fields.email')}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.email ? errors.email : undefined}
                required
              />
              
              <PhoneInput
            label={t('loan_form.fields.phone')}
                name="phone"
            value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.phone ? errors.phone : undefined}
                placeholder={t('loan_form.placeholders.phone')}
            required
          />
        </div>
            
            <div className="mt-6 sm:mt-8">
              <Checkbox
                label={
                  <span className="text-base">
                    {t('contact.form.agree_terms_prefix')}{' '}
                    <a 
                      href={`/documents/${getCurrentLanguage()}/privacy_policy.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 hover:underline"
                    >
                      {t('contact.form.privacy_policy_link')}
                    </a>
                    {' '}{t('contact.form.and')}{' '}
                    <a 
                      href={`/documents/${getCurrentLanguage()}/terms.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 hover:underline"
                    >
                      {t('contact.form.terms_link')}
                    </a>
                  </span>
                }
                checked={formData.agreeToTerms}
                onChange={(checked) => handleCheckboxChange('agreeToTerms', checked)}
                error={touched.agreeToTerms ? errors.agreeToTerms : undefined}
                className="text-base"
              />
            </div>
          </div>
        );
        
      case 2:
        return (
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-4 sm:mb-6 text-center">{t('loan_form.step_titles.loan_details')}</h3>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-6 sm:mb-8">{t('loan_form.step_descriptions.loan_details')}</p>

        <SelectField
          label={t('loan_form.fields.loan_type')}
          options={loanTypeOptions}
          value={formData.loanType}
              onChange={(value) => {
                handleSelectChange('loanType', String(value));
                setTouched(prev => ({ ...prev, loanType: true }));
              }}
              error={touched.loanType ? errors.loanType : undefined}
          placeholder={t('loan_form.placeholders.select_loan_type')}
          required
        />

            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-1">
                <Slider
                  label={t('loan_form.fields.loan_amount')}
                  min={minLoanAmount}
                  max={maxLoanAmount}
                  step={stepSize}
                  value={formData.loanAmount}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, loanAmount: value }));
                    setTouched(prev => ({ ...prev, loanAmount: true }));
                  }}
                  formatValue={(value) => (
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        value={value}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '') {
                            setFormData(prev => ({ ...prev, loanAmount: minLoanAmount }));
                          } else {
                            const numberValue = Number(inputValue);
                            if (!isNaN(numberValue)) {
                              setFormData(prev => ({ ...prev, loanAmount: numberValue }));
                            }
                          }
                          setTouched(prev => ({ ...prev, loanAmount: true }));
                        }}
                        onBlur={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '' || Number(inputValue) === 0) {
                            setFormData(prev => ({ ...prev, loanAmount: minLoanAmount }));
                          } else {
                            const newValue = Math.max(Number(inputValue), minLoanAmount);
                            setFormData(prev => ({ ...prev, loanAmount: Math.min(newValue, maxLoanAmount) }));
                          }
                          setTouched(prev => ({ ...prev, loanAmount: true }));
                        }}
                        className="w-24 px-2 py-1 text-secondary-500 font-medium border border-secondary-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary-500 focus:border-secondary-500 bg-white"
                        min={minLoanAmount}
                        max={maxLoanAmount}
                        step={stepSize}
                      />
                      <span>{t('loan_form.units.euro')}</span>
                    </div>
                  )}
                  unit=""
                />
              </div>
            </div>
            
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex-1">
                <Slider
                  label={t('loan_form.fields.loan_period')}
                  min={12}
                  max={maxLoanPeriod}
                  step={3}
                  value={formData.loanPeriod}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, loanPeriod: value }));
                    setTouched(prev => ({ ...prev, loanPeriod: true }));
                  }}
                  formatValue={(value) => (
                    <div className="flex items-center gap-1">
                      <input
            type="number"
                        value={value}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '') {
                            setFormData(prev => ({ ...prev, loanPeriod: 12 }));
                          } else {
                            const numberValue = Number(inputValue);
                            if (!isNaN(numberValue)) {
                              setFormData(prev => ({ ...prev, loanPeriod: numberValue }));
                            }
                          }
                          setTouched(prev => ({ ...prev, loanPeriod: true }));
                        }}
                        onBlur={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === '' || Number(inputValue) === 0) {
                            setFormData(prev => ({ ...prev, loanPeriod: 12 }));
                          } else {
                            const newValue = Math.max(Number(inputValue), 12);
                            setFormData(prev => ({ ...prev, loanPeriod: Math.min(newValue, maxLoanPeriod) }));
                          }
                          setTouched(prev => ({ ...prev, loanPeriod: true }));
                        }}
                        className="w-16 px-2 py-1 text-secondary-500 font-medium border border-secondary-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary-500 focus:border-secondary-500 bg-white"
                        min={12}
                        max={maxLoanPeriod}
                        step={3}
                      />
                      <span>{t('loan_form.units.months')}</span>
                    </div>
                  )}
                  unit=""
                />
              </div>
            </div>
          </div>
        );
        
      case 3:
        return (
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-4 sm:mb-6 text-center">{t('loan_form.step_titles.address_info')}</h3>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-6 sm:mb-8">{t('loan_form.step_descriptions.address_info')}</p>
            
          <InputField
              label={t('loan_form.fields.city')}
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={touched.city ? errors.city : undefined}
              placeholder={t('loan_form.placeholders.city')}
            required
          />
            
            <InputField
              label={t('loan_form.fields.declared_address')}
              name="declaredAddress"
              value={formData.declaredAddress}
              onChange={handleInputChange}
              onBlur={handleBlur}
              error={touched.declaredAddress ? errors.declaredAddress : undefined}
              placeholder={t('loan_form.placeholders.declared_address')}
          required
        />

          <Checkbox
              label={t('loan_form.fields.addresses_match')}
              checked={formData.addressesMatch}
              onChange={(checked) => handleCheckboxChange('addressesMatch', checked)}
              className="mb-3 sm:mb-4"
            />
            
            {!formData.addressesMatch && (
              <InputField
                label={t('loan_form.fields.factual_address')}
                name="facticalAddress"
                value={formData.facticalAddress}
                onChange={handleInputChange}
                onBlur={handleBlur}
                error={touched.facticalAddress ? errors.facticalAddress : undefined}
                placeholder={t('loan_form.placeholders.declared_address')}
              />
            )}
          </div>
        );
        
      case 4:
        return (
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-4 sm:mb-6 text-center">{t('loan_form.step_titles.family_info')}</h3>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-6 sm:mb-8">{t('loan_form.step_descriptions.family_info')}</p>
            
            <SelectField
              label={t('loan_form.fields.dependent_persons')}
              options={dependentPersonsOptions}
              value={formData.dependentPersons.toString()}
              onChange={(value) => {
                handleSelectChange('dependentPersons', String(value));
                setTouched(prev => ({ ...prev, dependentPersons: true }));
              }}
              error={touched.dependentPersons ? errors.dependentPersons : undefined}
              placeholder={t('loan_form.placeholders.select_count')}
              required
              className="mb-3 sm:mb-4"
            />

            <SelectField
              label={t('loan_form.fields.family_status')}
              options={familyStatusOptions}
              value={formData.familyStatus}
              onChange={(value) => {
                handleSelectChange('familyStatus', String(value));
                setTouched(prev => ({ ...prev, familyStatus: true }));
              }}
              error={touched.familyStatus ? errors.familyStatus : undefined}
              placeholder={t('loan_form.placeholders.select_status')}
              required
              className="mb-3 sm:mb-4"
            />

            <SelectField
              label={t('loan_form.fields.job_type')}
              options={jobTypeOptions}
              value={formData.jobType}
              onChange={(value) => {
                handleSelectChange('jobType', String(value));
                setTouched(prev => ({ ...prev, jobType: true }));
              }}
              error={touched.jobType ? errors.jobType : undefined}
              placeholder={t('loan_form.placeholders.select_job_type')}
              required
              className="mb-3 sm:mb-4"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <InputField
                label={t('loan_form.fields.monthly_income')}
                name="monthlyIncome"
                type="number"
                min={0}
                value={formData.monthlyIncome === '' ? '' : formData.monthlyIncome}
                onChange={(e) => {
                  const value = e.target.value === '' ? '' : Number(e.target.value);
                  setFormData(prev => ({ ...prev, monthlyIncome: value }));
                  setTouched(prev => ({ ...prev, monthlyIncome: true }));
                }}
                onBlur={handleBlur}
                error={touched.monthlyIncome ? errors.monthlyIncome : undefined}
                placeholder={t('loan_form.placeholders.monthly_income')}
                required
              />

              <InputField
                label={t('loan_form.fields.monthly_payments')}
                name="monthlyPayments"
                type="number"
                min={0}
                value={formData.monthlyPayments === '' ? '' : formData.monthlyPayments}
                onChange={(e) => {
                  const value = e.target.value === '' ? '' : Number(e.target.value);
                  setFormData(prev => ({ ...prev, monthlyPayments: value }));
                  setTouched(prev => ({ ...prev, monthlyPayments: true }));
                }}
                onBlur={handleBlur}
                error={touched.monthlyPayments ? errors.monthlyPayments : undefined}
                placeholder={t('loan_form.placeholders.monthly_payments')}
                required
              />
            </div>

            {/* Has Real Estate */}
            <div className="mt-6 text-left">
              <label className="block text-base font-semibold text-gray-700 mb-2">
                {t('loan_form.fields.has_real_estate')}
              </label>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  className={`px-6 py-2 text-base font-medium transition-colors ${
                    formData.hasRealEstate
                      ? 'bg-secondary-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, hasRealEstate: true }));
                    setTouched(prev => ({ ...prev, hasRealEstate: true }));
                  }}
                >
                  {t('loan_form.yes')}
                </button>
                <button
                  type="button"
                  className={`px-6 py-2 text-base font-medium border-l border-gray-300 transition-colors ${
                    !formData.hasRealEstate
                      ? 'bg-secondary-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, hasRealEstate: false }));
                    setTouched(prev => ({ ...prev, hasRealEstate: true }));
                  }}
                >
                  {t('loan_form.no')}
                </button>
              </div>
            </div>

            {/* Is Politically Important */}
            <div className="mt-6 text-left">
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-base font-semibold text-gray-700">
                  {t('loan_form.fields.is_politically_important')}
                </label>
                <div className="relative group">
                  <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold cursor-help">
                    i
                  </div>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    {t('loan_form.fields.is_politically_important_tooltip')}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </div>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  className={`px-6 py-2 text-base font-medium transition-colors ${
                    formData.isPoliticallyImportant
                      ? 'bg-secondary-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, isPoliticallyImportant: true }));
                    setTouched(prev => ({ ...prev, isPoliticallyImportant: true }));
                  }}
                >
                  {t('loan_form.yes')}
                </button>
                <button
                  type="button"
                  className={`px-6 py-2 text-base font-medium border-l border-gray-300 transition-colors ${
                    !formData.isPoliticallyImportant
                      ? 'bg-secondary-500 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, isPoliticallyImportant: false }));
                    setTouched(prev => ({ ...prev, isPoliticallyImportant: true }));
                  }}
                >
                  {t('loan_form.no')}
                </button>
              </div>
            </div>
          </div>
        );
        
      case 5:
        return (
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-4 sm:mb-6 text-center">{t('loan_form.step_titles.bank_verification')}</h3>
            <p className="text-sm sm:text-base text-gray-600 text-center mb-6 sm:mb-8">{t('loan_form.step_descriptions.bank_verification')}</p>
            
            <div className="mt-4 sm:mt-6">
              <div className="mb-2">  
              <Checkbox
                label={t('loan_form.fields.bank_authorization')}
                checked={formData.authorizeBankAccess}
                onChange={(checked) => handleCheckboxChange('authorizeBankAccess', checked)}
                error={touched.authorizeBankAccess ? errors.authorizeBankAccess : undefined}
                labelClassName="text-base"
              />
              </div>

              {formData.authorizeBankAccess && (
                <BankSelection
                  label={t('loan_form.fields.select_bank')}
                  banks={bankInstitutions}
                  value={formData.selectedBank}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, selectedBank: value }));
                    setTouched(prev => ({ ...prev, selectedBank: true }));
                  }}
                  error={touched.selectedBank ? errors.selectedBank : undefined}
                  loading={loadingInstitutions}
                  placeholder={t('loan_form.placeholders.select_bank')}
                  required={formData.authorizeBankAccess}
                />
              )}
            </div>
            
            {!formData.authorizeBankAccess && (
              <div className="mt-4 sm:mt-6">
                <InputField
                  label={t('loan_form.fields.bank_account')}
                  name="bankAccount"
                  value={formData.bankAccount}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  error={touched.bankAccount ? errors.bankAccount : undefined}
                  placeholder={t('loan_form.placeholders.bank_account')}
                />

                <FileUpload
                  label={t('loan_form.fields.documents')}
                  onChange={handleFileChange}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  multiple={true}
                  maxSize={5 * 1024 * 1024}
                  error={touched.documents ? errors.documents : undefined}
                  messages={{
                    error: t('loan_form.file_upload.error'),
                    success: t('loan_form.file_upload.success'),
                    removed: t('loan_form.file_upload.removed'),
                    max: t('loan_form.file_upload.max'),
                    size: t('loan_form.file_upload.size'),
                    type: t('loan_form.file_upload.type')
                  }}
                />
              </div>
            )}

            <div className="mt-6 sm:mt-8">
          <Checkbox
                label={
                  <span className="text-base">
                    {t('contact.form.agree_terms_prefix')}{' '}
                    <a 
                      href={`/documents/${getCurrentLanguage()}/privacy_policy.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 hover:underline"
                    >
                      {t('contact.form.privacy_policy_link')}
                    </a>
                    {' '}{t('contact.form.and')}{' '}
                    <a 
                      href={`/documents/${getCurrentLanguage()}/terms.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-500 hover:underline"
                    >
                      {t('contact.form.terms_link')}
                    </a>
                  </span>
                }
                checked={formData.agreeToTerms}
                onChange={(checked) => handleCheckboxChange('agreeToTerms', checked)}
                error={touched.agreeToTerms ? errors.agreeToTerms : undefined}
                className="text-base"
              />
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className={`w-full h-full flex flex-col ${isInPopup ? 'bg-transparent' : 'bg-white max-w-4xl mx-auto box-border p-6 md:p-10 rounded-2xl shadow-2xl'}`} id="loan-application-form">
      <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center ${isInPopup ? 'mb-3 sm:mb-6' : 'mb-4 sm:mb-8'} gap-2 sm:gap-0`}>
        <h2 className={`${isInPopup ? 'text-lg sm:text-2xl' : 'text-xl sm:text-3xl'} font-bold text-primary-500`}>{t('loan_form.title')}</h2>
        {submissionLimit.remainingSubmissions < 2 && submissionLimit.timeUntilReset > 0 && (
          <div className="text-xs sm:text-sm text-gray-600 bg-gray-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-medium self-start sm:self-auto">
            <div className="flex flex-col sm:flex-row sm:gap-1 text-center sm:text-left">
              <span>{t('loan_form.remaining_applications')} {submissionLimit.remainingSubmissions}</span>
              <span className="hidden sm:inline">•</span>
              <span className="text-gray-500">{formatTimeUntilReset(submissionLimit.timeUntilReset)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Flash Messages */}
      {flashAlert.visible && (
        <div className="mb-4 sm:mb-6">
          <Alert
            type={flashAlert.type}
            message={flashAlert.message}
            visible={flashAlert.visible}
            onClose={() => setFlashAlert({ ...flashAlert, visible: false })}
            autoClose={flashAlert.type !== 'success'}
            autoCloseTime={5000}
          />
        </div>
      )}
      
      {!submissionLimit.canSubmit && !isSuccessfullySubmitted && (
        <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-xl">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-error-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="flex flex-col">
              <span className="text-error-800 font-medium">
                {t('loan_form.limit_exceeded')}
              </span>
              <span className="text-error-700 text-sm mt-1">
                {t('loan_form.try_again_in')}: {formatTimeUntilReset(submissionLimit.timeUntilReset)}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Step Progress Indicator */}
      <div className={`${isInPopup ? 'mb-4 sm:mb-6' : 'mb-6 sm:mb-8'}`}>
        <div className="flex justify-between items-center relative">
          {/* Progress Line */}
          <div className="absolute top-4 sm:top-6 left-0 right-0 h-0.5 bg-gray-200 z-0">
            <div 
              className="h-full bg-success-500 transition-all duration-500 ease-in-out"
              style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
            />
          </div>
          
          {[1, 2, 3, 4, 5].map((step) => {
            let stepBg, stepText, stepBorder;
            
            if (step < currentStep) {
              // Completed steps - green
              stepBg = 'bg-success-500';
              stepText = 'text-white';
              stepBorder = 'border-success-500';
            } else if (step === currentStep) {
              // Active step - violet
              stepBg = 'bg-primary-500';
              stepText = 'text-white';
              stepBorder = 'border-primary-500';
            } else {
              // Future steps - gray
              stepBg = 'bg-white';
              stepText = 'text-gray-400';
              stepBorder = 'border-gray-300';
            }
            
            return (
              <div
                key={step}
                className={`flex flex-col items-center z-10 ${isInPopup ? 'bg-transparent' : 'bg-white'} flex-1`}
              >
                <div
                  className={`w-8 h-8 sm:w-12 sm:h-12 rounded-full border-2 flex items-center justify-center text-sm sm:text-lg font-bold transition-all duration-300 ${stepBg} ${stepText} ${stepBorder} shadow-lg`}
                >
                  {step < currentStep ? (
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    step
                  )}
                </div>
                
                <div className="mt-2 sm:mt-3 text-center max-w-14 sm:max-w-20">
                  <div className={`text-xs sm:text-sm font-medium leading-tight ${step <= currentStep ? 'text-gray-900' : 'text-gray-500'}`}>
                    {step === 1 && (
                      <>
                        <span className="sm:hidden">{t('loan_form.step_labels.personal_short')}</span>
                        <span className="hidden sm:inline">{t('loan_form.step_labels.personal_full')}</span>
                      </>
                    )}
                    {step === 2 && (
                      <>
                        <span className="sm:hidden">{t('loan_form.step_labels.loan_short')}</span>
                        <span className="hidden sm:inline">{t('loan_form.step_labels.loan_full')}</span>
                      </>
                    )}
                    {step === 3 && (
                      <>
                        <span className="sm:hidden">{t('loan_form.step_labels.address_short')}</span>
                        <span className="hidden sm:inline">{t('loan_form.step_labels.address_full')}</span>
                      </>
                    )}
                    {step === 4 && (
                      <>
                        <span className="sm:hidden">{t('loan_form.step_labels.family_short')}</span>
                        <span className="hidden sm:inline">{t('loan_form.step_labels.family_full')}</span>
                      </>
                    )}
                    {step === 5 && (
                      <>
                        <span className="sm:hidden">{t('loan_form.step_labels.bank_short')}</span>
                        <span className="hidden sm:inline">{t('loan_form.step_labels.bank_full')}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        <fieldset disabled={!submissionLimit.canSubmit} className="flex-1 flex flex-col">
          {/* Form Content */}
          <div className={`flex-1 ${isInPopup ? 'bg-gray-50' : 'bg-gray-50'} rounded-xl p-2 sm:p-4 md:p-6 ${isInPopup ? 'mb-3 sm:mb-6' : 'mb-4 sm:mb-8'} ${isInPopup ? 'min-h-80 sm:min-h-[28rem]' : 'min-h-72 sm:min-h-96'}`}>
            {renderStep()}
          </div>
        </fieldset>

        {/* Navigation Buttons */}
        <div className={`flex justify-between items-center pt-3 sm:pt-6 ${isInPopup ? 'border-t border-gray-100' : 'border-t border-gray-200'}`}>
          {currentStep > 1 ? (
            <Button
              type="button"
              variant="secondary"
              onClick={handlePrevStep}
              size="lg"
              className={`px-3 sm:px-8 py-2 sm:py-3 font-medium text-xs sm:text-base ${isSuccessfullySubmitted ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSuccessfullySubmitted}
            >
              <span className="sm:hidden">{t('loan_form.buttons.back_short')}</span>
              <span className="hidden sm:inline">{t('loan_form.buttons.back_full')}</span>
            </Button>
          ) : (
            <div></div>
          )}
          
          {currentStep < 5 ? (
            <Button
              type="button"
              variant="primary"
              onClick={(e) => handleNextStep(e)}
              size="lg"
              className={`px-3 sm:px-8 py-2 sm:py-3 font-medium text-xs sm:text-base ${isSuccessfullySubmitted ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'bg-primary-500 hover:bg-primary-600'}`}
              disabled={isSuccessfullySubmitted || (currentStep === 1 && !submissionLimit.canSubmit)}
            >
              <span className="sm:hidden">{t('loan_form.buttons.next_short')}</span>
              <span className="hidden sm:inline">{t('loan_form.buttons.next_full')}</span>
            </Button>
          ) : (
        <Button
          type={isSuccessfullySubmitted ? "button" : "submit"}
              variant="primary"
          size="lg"
              className={`px-3 sm:px-8 py-2 sm:py-3 font-medium text-xs sm:text-base ${isSuccessfullySubmitted ? 'bg-primary-500 hover:bg-primary-600' : 'bg-success-500 hover:bg-success-600'}`}
              disabled={!isSuccessfullySubmitted && !submissionLimit.canSubmit}
              onClick={isSuccessfullySubmitted ? () => {
                if (onClose) {
                  onClose();
                } else {
                  window.close();
                }
              } : undefined}
        >
              <span className="sm:hidden">{isSuccessfullySubmitted ? t('loan_form.buttons.close_short') || 'Close' : (submissionLimit.canSubmit ? t('loan_form.buttons.submit_short') : 'Limits')}</span>
              <span className="hidden sm:inline">{isSuccessfullySubmitted ? t('loan_form.buttons.close_full') || 'Close Form' : (submissionLimit.canSubmit ? t('loan_form.buttons.submit_full') : t('loan_form.buttons.limit_exceeded'))}</span>
        </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default LoanForm;
